import { useMemo, useRef, useState } from 'react';
import { ArrowDownAZ, ArrowUpAZ, Check, Search, X } from 'lucide-react';
import { R, ft, label, shadow } from '../lib/ui';
import { AnchoredPopover } from './AnchoredPopover';

export type Sort = { colId: string; dir: 'asc' | 'desc' } | null;

/** Флажок из оригинала: квадрат с галочкой, акцентная заливка. */
function Checkbox({ checked, size = 16 }: { checked: boolean; size?: number }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        border: `1px solid ${checked ? R.accent : R.border}`,
        background: checked ? R.accent : R.bgInput,
        transition: 'background .1s, border-color .1s',
      }}
    >
      {checked && <Check size={size - 4} color="#fff" strokeWidth={3} />}
    </span>
  );
}

function Row({
  active, onClick, children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 8px',
        textAlign: 'left',
        fontSize: 12.5,
        color: active ? R.accent : R.text,
        background: active ? R.accentSubtle : 'transparent',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = R.bgElevated;
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

/**
 * Кнопка в шапке колонки: сортировка и фильтр по значениям.
 * Набор значений накапливается по всем строкам листа, пустые показываются
 * как «(пусто)» — иначе их нельзя было бы отфильтровать.
 */
export function ColumnFilter({
  colId, title, values, sort, filter, onSort, onFilter,
}: {
  colId: string;
  title: string;
  /** Уникальные значения колонки, уже отсортированные. */
  values: string[];
  sort: Sort;
  /** null — фильтра нет; иначе набор разрешённых значений. */
  filter: Set<string> | null;
  onSort: (sort: Sort) => void;
  onFilter: (filter: Set<string> | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const ref = useRef<HTMLButtonElement>(null);

  const sorted = sort?.colId === colId;
  const filtered = !!filter;

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? values.filter((v) => v.toLowerCase().includes(term)) : values;
  }, [values, query]);

  const checked = draft ?? filter ?? new Set(values);

  const openMenu = () => {
    setDraft(filter ? new Set(filter) : new Set(values));
    setQuery('');
    setOpen(true);
  };

  const toggle = (value: string) => {
    const next = new Set(checked);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setDraft(next);
  };

  const apply = () => {
    // Отмечено всё — значит фильтра нет.
    const all = values.every((v) => checked.has(v));
    onFilter(all ? null : new Set(checked));
    setOpen(false);
  };

  return (
    <>
      <button
        ref={ref}
        title="Сортировать/фильтровать"
        onClick={(e) => {
          e.stopPropagation();
          openMenu();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          marginLeft: 4,
          flex: '0 0 18px',
          color: sorted || filtered ? R.accent : R.textMuted,
          background: sorted || filtered ? R.accentSubtle : 'transparent',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => {
          if (!sorted && !filtered) e.currentTarget.style.background = R.bgElevated;
        }}
        onMouseLeave={(e) => {
          if (!sorted && !filtered) e.currentTarget.style.background = 'transparent';
        }}
      >
        {sorted && sort?.dir === 'desc' ? <ArrowDownAZ size={12} /> : <ArrowUpAZ size={12} />}
      </button>

      <AnchoredPopover anchorRef={ref} open={open} onClose={() => setOpen(false)}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 260,
            display: 'flex',
            flexDirection: 'column',
            background: R.bgPanel,
            border: `2px solid ${R.border}`,
            boxShadow: shadow.dropdown,
            padding: 6,
          }}
        >
          <div style={{ ...label, padding: '4px 8px 6px' }}>{title}</div>

          <Row
            active={sorted && sort?.dir === 'asc'}
            onClick={() => {
              onSort({ colId, dir: 'asc' });
              setOpen(false);
            }}
          >
            <ArrowUpAZ size={14} /> Сортировать А → Я
          </Row>
          <Row
            active={sorted && sort?.dir === 'desc'}
            onClick={() => {
              onSort({ colId, dir: 'desc' });
              setOpen(false);
            }}
          >
            <ArrowDownAZ size={14} /> Сортировать Я → А
          </Row>
          {sorted && (
            <Row
              onClick={() => {
                onSort(null);
                setOpen(false);
              }}
            >
              <X size={14} /> Убрать сортировку
            </Row>
          )}

          <div style={{ height: 1, background: R.border, margin: '6px 0' }} />

          <div style={{ position: 'relative', marginBottom: 6 }}>
            <Search
              size={13}
              color={R.textMuted}
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск значений…"
              style={{
                width: '100%',
                padding: '6px 8px 6px 28px',
                fontSize: 12,
                background: R.bgInput,
                color: R.text,
                border: `1px solid ${R.border}`,
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              padding: '2px 4px',
              marginBottom: 4,
            }}
          >
            <button
              onClick={() => setDraft(new Set(values))}
              style={{ color: R.accentText, fontSize: 11, background: 'transparent' }}
            >
              Выбрать все
            </button>
            <button
              onClick={() => setDraft(new Set())}
              style={{ color: R.accentText, fontSize: 11, background: 'transparent' }}
            >
              Очистить
            </button>
          </div>

          <div
            className="rt-scroll"
            style={{
              maxHeight: 200,
              overflowY: 'auto',
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {shown.map((value) => (
              <label
                key={value}
                onClick={() => toggle(value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 6px',
                  fontSize: 12.5,
                  color: R.text,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = R.bgElevated)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <Checkbox checked={checked.has(value)} />
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {value === '' ? <span style={{ color: R.textMuted }}>(пусто)</span> : value}
                </span>
              </label>
            ))}

            {shown.length === 0 && (
              <div
                style={{ padding: 8, color: R.textMuted, fontSize: 12, textAlign: 'center' }}
              >
                Ничего не найдено
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 6,
              marginTop: 8,
              paddingTop: 8,
              borderTop: ft.hair,
              flex: '0 0 auto',
            }}
          >
            {filtered && (
              <button
                onClick={() => {
                  onFilter(null);
                  setOpen(false);
                }}
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  color: R.text,
                  background: 'transparent',
                  border: `1px solid ${R.border}`,
                  cursor: 'pointer',
                }}
              >
                Снять фильтр
              </button>
            )}

            <button
              onClick={apply}
              style={{
                marginLeft: 'auto',
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 800,
                color: R.onAccent,
                background: R.accent,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <Check size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
              Применить
            </button>
          </div>
        </div>
      </AnchoredPopover>
    </>
  );
}
